'use client';

import React, { useState, useEffect, useRef } from 'react';
import Button from '@/components/ui/Button';
import Image from 'next/image';
import { sendBookingRequest } from '@/lib/booking-api';

interface UniversalBookingModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialServices?: string[];
    blockName: string;
}

interface FormData {
    date: string;
    services: string[];
    phone: string;
}

interface ValidationErrors {
    date?: string;
    phone?: string;
}

const services = [
    'Замена масла в ДВС',
    'Замена масляного фильтра',
    'Замена воздушного фильтра',
    'Замена масла в АКПП',
    'Замена тормозной жидкости',
    'Смена шин'
];

export default function UniversalBookingModal({
    isOpen,
    onClose,
    initialServices = [],
    blockName,
}: UniversalBookingModalProps) {
    const [formData, setFormData] = useState<FormData>({
        date: '',
        services: [],
        phone: '',
    });
    
    const [customService, setCustomService] = useState('');
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const privacyCheckboxRef = useRef<HTMLInputElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Инициализация услуг при открытии модального окна
    useEffect(() => {
        if (isOpen) {
            if (initialServices && initialServices.length > 0) {
                setFormData(prev => ({
                    ...prev,
                    services: initialServices,
                }));
                setCustomService(initialServices.join(', '));
            } else {
                // Сбрасываем при открытии без начальных услуг
                setFormData({
                    date: '',
                    services: [],
                    phone: '',
                });
                setCustomService('');
            }
            // Сбрасываем согласие при открытии модального окна
            setPrivacyAgreed(false);
            setSubmitError(null);
            setSubmitSuccess(false);
            setShowSuccessModal(false);
        }
    }, [isOpen]);

    const getInputNumbersValue = (input: HTMLInputElement) => {
        return input.value.replace(/\D/g, '');
    };

    const onPhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        let input = e.currentTarget;
        let inputNumbersValue = getInputNumbersValue(input);
        let pasted = e.clipboardData;
        if (pasted) {
            let pastedText = pasted.getData('Text');
            if (/\D/g.test(pastedText)) {
                input.value = inputNumbersValue;
                return;
            }
        }
    };

    const onPhoneInput = (e: React.FormEvent<HTMLInputElement>) => {
        let input = e.currentTarget;
        let inputNumbersValue = getInputNumbersValue(input);
        let selectionStart = input.selectionStart || 0;
        let formattedInputValue = '';

        if (!inputNumbersValue) {
            input.value = '';
            return;
        }

        if (input.value.length !== selectionStart) {
            const event = e.nativeEvent as InputEvent;
            if (event.data && /\D/g.test(event.data)) {
                input.value = inputNumbersValue;
            }
            return;
        }

        if (['7', '8', '9'].indexOf(inputNumbersValue[0]) > -1) {
            if (inputNumbersValue[0] === '9') inputNumbersValue = '7' + inputNumbersValue;
            let firstSymbols = inputNumbersValue[0] === '8' ? '8' : '+7';
            formattedInputValue = firstSymbols + ' ';
            if (inputNumbersValue.length > 1) {
                formattedInputValue += '(' + inputNumbersValue.substring(1, 4);
            }
            if (inputNumbersValue.length >= 5) {
                formattedInputValue += ') ' + inputNumbersValue.substring(4, 7);
            }
            if (inputNumbersValue.length >= 8) {
                formattedInputValue += '-' + inputNumbersValue.substring(7, 9);
            }
            if (inputNumbersValue.length >= 10) {
                formattedInputValue += '-' + inputNumbersValue.substring(9, 11);
            }
        } else {
            formattedInputValue = '+' + inputNumbersValue.substring(0, 16);
        }
        input.value = formattedInputValue;
        setFormData(prev => ({ ...prev, phone: formattedInputValue }));
        
        // Очищаем ошибку валидации при вводе
        if (validationErrors.phone) {
            setValidationErrors(prev => ({ ...prev, phone: undefined }));
        }
    };

    const onPhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        if (!input.value) {
            input.value = '+7 ';
            setFormData(prev => ({ ...prev, phone: '+7 ' }));
        }
    };

    const onPhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        let inputValue = e.currentTarget.value.replace(/\D/g, '');
        if (e.key === 'Backspace' && inputValue.length === 1) {
            e.currentTarget.value = '';
            setFormData(prev => ({ ...prev, phone: '' }));
        }
    };

    const validateDate = (dateString: string): string | null => {
        if (dateString.length !== 10) {
            return 'Введите полную дату';
        }

        const parts = dateString.split('.');
        if (parts.length !== 3) {
            return 'Неверный формат даты';
        }

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) {
            return 'Неверный формат даты';
        }

        if (month < 1 || month > 12) {
            return 'Месяц должен быть от 1 до 12';
        }

        if (day < 1 || day > 31) {
            return 'День должен быть от 1 до 31';
        }

        // Названия месяцев в предложном падеже
        const monthNames = [
            'январе', 'феврале', 'марте', 'апреле', 'мае', 'июне',
            'июле', 'августе', 'сентябре', 'октябре', 'ноябре', 'декабре'
        ];

        // Проверка количества дней в месяце
        const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
        
        // Проверка високосного года
        const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
        if (isLeapYear) {
            daysInMonth[1] = 29;
        }

        if (day > daysInMonth[month - 1]) {
            return `В ${monthNames[month - 1]} не может быть ${day} дней`;
        }

        const currentYear = new Date().getFullYear();
        const maxYear = currentYear + 5; // Максимум на 5 лет вперёд
        if (year < currentYear || year > maxYear) {
            return `Год должен быть от ${currentYear} до ${maxYear}`;
        }

        // Проверка, что дата не в прошлом
        const selectedDate = new Date(year, month - 1, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (selectedDate < today) {
            return 'Нельзя выбрать прошедшую дату';
        }

        return null;
    };

    const onDateInput = (e: React.FormEvent<HTMLInputElement>) => {
        let input = e.currentTarget;
        let value = input.value.replace(/\D/g, '');
        let formattedValue = '';

        if (value.length > 0) {
            formattedValue = value.substring(0, 2);
        }
        if (value.length >= 3) {
            formattedValue += '.' + value.substring(2, 4);
        }
        if (value.length >= 5) {
            formattedValue += '.' + value.substring(4, 8);
        }

        input.value = formattedValue;
        setFormData(prev => ({ ...prev, date: formattedValue }));
        
        // Валидация только если дата полностью введена
        if (formattedValue.length === 10) {
            const error = validateDate(formattedValue);
            setValidationErrors(prev => ({ ...prev, date: error || undefined }));
        } else {
            setValidationErrors(prev => ({ ...prev, date: undefined }));
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleServiceToggle = (service: string) => {
        const isCurrentlySelected = formData.services.includes(service);
        let newServices: string[];
        
        if (isCurrentlySelected) {
            // Удаляем услугу
            newServices = formData.services.filter(s => s !== service);
        } else {
            // Добавляем услугу
            newServices = [...formData.services, service];
        }
        
        setFormData(prev => ({
            ...prev,
            services: newServices
        }));
        
        // Обновляем текстовое поле: сохраняем все услуги, включая кастомные
        setCustomService(newServices.join(', '));
    };

    const validatePhone = (phone: string): string | null => {
        const phoneDigits = phone.replace(/\D/g, '');
        if (!phone || phoneDigits.length < 11) {
            return 'Введите корректный номер телефона';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Проверка согласия с политикой конфиденциальности
        if (!privacyAgreed) {
            return;
        }
        
        // Проверка валидации даты перед отправкой
        const dateError = validateDate(formData.date);
        if (dateError) {
            setValidationErrors(prev => ({ ...prev, date: dateError }));
            return;
        }
        
        // Проверка валидации телефона перед отправкой
        const phoneError = validatePhone(formData.phone);
        if (phoneError) {
            setValidationErrors(prev => ({ ...prev, phone: phoneError }));
            return;
        }
        
        try {
            setIsSubmitting(true);
            setSubmitError(null);
            setSubmitSuccess(false);

            const response = await sendBookingRequest({
                phone: formData.phone,
                date: formData.date,
                services: formData.services,
                customService,
                blockName,
            });

            if (!response.success) {
                setSubmitError(response.error || 'Не удалось отправить заявку. Попробуйте ещё раз.');
                return;
            }

            setSubmitSuccess(true);
            setShowSuccessModal(true);
        } catch (error) {
            setSubmitError('Не удалось отправить заявку. Попробуйте ещё раз.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
            onClick={handleOverlayClick}
        >
            <div 
                className="bg-white rounded-[20px] shadow-xl w-full max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Фиксированный заголовок с изображением */}
                <div className="relative flex-shrink-0">
                    {/* Кнопка закрытия */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-lg transition-colors"
                        aria-label="Закрыть"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="#1d1d21" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>

                    {/* Фоновое изображение с текстом */}
                    <div className="relative h-[280px] sm:h-[320px] rounded-t-[20px] overflow-hidden">
                        <Image
                            src="/images/universal-form/universal-form.jpg"
                            alt="Форма записи"
                            fill
                            className="object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent from-0% to-[rgba(40,40,40,0.7)] to-100%" />
                        
                        <div className="absolute inset-0 flex flex-col items-center justify-end text-center px-6 pb-6 sm:pb-8">
                            <h2 className="text-[28px] sm:text-[36px] lg:text-[42px] font-bold text-white leading-[1.1] mb-3 sm:mb-4">
                                Добро пожаловать!
                            </h2>
                            <p className="text-[14px] sm:text-[16px] text-white leading-[1.3] max-w-[380px]">
                                Укажите желаемые даты обслуживания,<br className="hidden sm:block" />
                                услуги и ваш контакт, и мы свяжемся<br className="hidden sm:block" />
                                для уточнения деталей
                            </p>
                        </div>
                    </div>
                </div>

                {/* Прокручиваемая форма */}
                <div className="overflow-y-auto flex-1">
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Поле даты */}
                    <div className="bg-gray-100 rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <div className="text-[#b89d7e] font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                            Желаемая дата
                        </div>
                        <input
                            type="text"
                            placeholder="дд.мм.гггг"
                            maxLength={10}
                            onInput={onDateInput}
                            required
                            className={`w-full bg-white border rounded-[5px] px-4 py-3 text-[16px] lg:text-[18px] text-[#1d1d21] focus:border-[#b89d7e] outline-none ${
                                validationErrors.date ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {validationErrors.date && (
                            <div className="text-red-500 text-[12px] lg:text-[14px] mt-2 leading-tight">
                                {validationErrors.date}
                            </div>
                        )}
                    </div>

                    {/* Выбор услуг */}
                    <div className="bg-gray-100 rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <div className="text-[#b89d7e] font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                            УКАЖИТЕ УСЛУГУ
                        </div>
                        <div className="space-y-2">
                            {services.map((service, index) => (
                                <label
                                    key={index}
                                    className="flex items-center cursor-pointer group relative"
                                >
                                    <div className="relative mr-3" style={{ width: 'clamp(16px, 1.25vw, 20px)', height: 'clamp(16px, 1.25vw, 20px)' }}>
                                        <input
                                            type="checkbox"
                                            checked={formData.services.includes(service)}
                                            onChange={() => handleServiceToggle(service)}
                                            className="absolute inset-0 w-full h-full cursor-pointer border border-gray-300 rounded focus:ring-[#b89d7e] hover:scale-110 transition-all appearance-none checked:bg-[#b89d7e] checked:border-[#b89d7e] z-0"
                                        />
                                        {formData.services.includes(service) && (
                                            <svg
                                                className="absolute inset-0 w-full h-full pointer-events-none z-10"
                                                fill="none"
                                                stroke="white"
                                                strokeWidth="2.5"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="text-[14px] lg:text-[16px] text-[#1d1d21] group-hover:text-[#b89d7e] transition-colors leading-[1.3]">
                                        {service}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className="mt-4">
                            <div className="text-[#b89d7e] font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                                Или укажите услугу вручную
                            </div>
                            <textarea
                                value={customService}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    setCustomService(newValue);
                                    // Обновляем services из текстового поля
                                    const servicesFromText = newValue
                                        .split(',')
                                        .map(s => s.trim())
                                        .filter(s => s.length > 0);
                                    setFormData(prev => ({
                                        ...prev,
                                        services: servicesFromText,
                                    }));
                                }}
                                placeholder="Введите услуги через запятую"
                                className="w-full bg-white border border-gray-300 rounded-[5px] px-4 py-3 text-[14px] lg:text-[16px] text-[#1d1d21] focus:border-[#b89d7e] outline-none resize-none"
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Поле телефона */}
                    <div className="bg-gray-100 rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <div className="text-[#b89d7e] font-bold text-[14px] lg:text-[16px] uppercase mb-3 leading-tight">
                            ВАШ КОНТАКТ
                        </div>
                        <input
                            type="tel"
                            placeholder="+7 (XXX) XXX-XX-XX"
                            onFocus={onPhoneFocus}
                            onInput={onPhoneInput}
                            onPaste={onPhonePaste}
                            onKeyDown={onPhoneKeyDown}
                            required
                            className={`w-full bg-white border rounded-[5px] px-4 py-3 text-[16px] lg:text-[18px] text-[#1d1d21] focus:border-[#b89d7e] outline-none ${
                                validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        {validationErrors.phone && (
                            <div className="text-red-500 text-[12px] lg:text-[14px] mt-2 leading-tight">
                                {validationErrors.phone}
                            </div>
                        )}
                    </div>

                    {/* Чекбокс согласия с политикой */}
                    <div className="bg-gray-100 rounded-lg lg:rounded-2xl p-4 lg:p-5">
                        <label 
                            className="flex items-start cursor-pointer group relative"
                        >
                            <div className="relative mr-3 flex-shrink-0" style={{ width: 'clamp(16px, 1.25vw, 20px)', height: 'clamp(16px, 1.25vw, 20px)' }}>
                                <input
                                    ref={privacyCheckboxRef}
                                    type="checkbox"
                                    checked={privacyAgreed}
                                    onChange={(e) => {
                                        setPrivacyAgreed(e.target.checked);
                                    }}
                                    required
                                    className="absolute inset-0 w-full h-full cursor-pointer border border-gray-300 rounded focus:ring-[#b89d7e] hover:scale-110 transition-all appearance-none checked:bg-[#b89d7e] checked:border-[#b89d7e] z-0"
                                />
                                {privacyAgreed && (
                                    <svg
                                        className="absolute inset-0 w-full h-full pointer-events-none z-10"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="2.5"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>
                            <span className="text-[14px] lg:text-[16px] text-[#1d1d21] leading-[1.3]">
                                Я согласен с{' '}
                                <a
                                    href="/files/privacy-policy.pdf"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#b89d7e] hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    политикой обработки персональных данных
                                </a>
                            </span>
                        </label>
                    </div>

                    {/* Кнопка отправки */}
                    <button
                        type="submit"
                        className={`w-full py-3 px-4 text-[20px] font-normal tracking-[-0.02em] border border-[#b89d7e] text-[#b89d7e] rounded-[50px] transition-all duration-200 ${
                            isSubmitting || submitSuccess || !privacyAgreed
                                ? 'opacity-60 cursor-not-allowed'
                                : 'hover:bg-[#b89d7e] hover:text-white'
                        }`}
                        disabled={isSubmitting || submitSuccess || !privacyAgreed}
                    >
                        {submitSuccess
                            ? 'Отправлено'
                            : isSubmitting
                                ? 'Отправка...'
                                : 'Заявка на запись'}
                    </button>
                    {submitError && (
                        <div className="text-red-500 text-[14px] mt-2 text-center">
                            {submitError}
                        </div>
                    )}
                </form>
                </div>
            </div>
            
            {/* Модальное окно успешной отправки */}
            {showSuccessModal && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
                    onClick={() => setShowSuccessModal(false)}
                >
                    <div 
                        className="bg-white rounded-[20px] shadow-xl w-full max-w-[500px] p-8 text-center relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowSuccessModal(false)}
                            className="absolute top-4 right-4 text-[#1d1d21] hover:opacity-80 text-2xl"
                            style={{ fontSize: '28px', lineHeight: '1' }}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        
                        <h3 
                            className="text-[18px] sm:text-[20px] font-medium text-[#1d1d21] mb-4"
                            style={{ fontFamily: 'var(--font-manrope, Manrope, sans-serif)' }}
                        >
                            Заявка на запись сформирована!
                        </h3>
                        
                        <p 
                            className="text-[18px] sm:text-[20px] font-medium text-[#1d1d21] leading-relaxed"
                            style={{ fontFamily: 'var(--font-manrope, Manrope, sans-serif)' }}
                        >
                            Мы свяжемся с Вами для уточнения времени!
                        </p>
                        
                        <p 
                            className="text-[18px] sm:text-[20px] font-medium text-[#1d1d21] mb-6"
                            style={{ fontFamily: 'var(--font-manrope, Manrope, sans-serif)' }}
                        >
                            Спасибо!
                        </p>
                        
                        <button
                            onClick={() => {
                                setShowSuccessModal(false);
                                onClose();
                            }}
                            className="w-full py-3 px-4 text-[18px] font-normal tracking-[-0.02em] bg-[#b89d7e] text-white rounded-[50px] transition-all duration-200 hover:bg-[#a68d6f]"
                            style={{ fontFamily: 'var(--font-manrope, Manrope, sans-serif)' }}
                        >
                            Закрыть
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}